import React, { useEffect, useState } from "react";
import { dashboardReport, verifactuChain } from "@/lib/api";
import { Banknote, CreditCard, Smartphone, Wallet, User2, ShieldCheck, Link2, TrendingUp, Receipt } from "lucide-react";

const METHOD_ICONS = {
  efectivo: Banknote,
  tarjeta: CreditCard,
  bizum: Smartphone,
  vale: Wallet,
  credito: User2,
};

export default function Reports() {
  const [data, setData] = useState(null);
  const [chain, setChain] = useState(null);

  useEffect(() => {
    dashboardReport().then(setData);
    verifactuChain().then(setChain);
  }, []);

  if (!data) {
    return <div className="p-10 text-center text-slate-400">Cargando informes…</div>;
  }

  return (
    <div className="h-full overflow-y-auto thin-scroll p-6 bg-slate-50">
      <div className="max-w-6xl mx-auto space-y-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-slate-500">Cuadro de mando</div>
          <h1 className="font-display text-3xl font-semibold text-slate-900">Informes · {data.date}</h1>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi icon={TrendingUp} label="Ventas hoy" value={`${data.total_today.toFixed(2)}€`} tone="emerald" />
          <Kpi icon={Receipt} label="Tickets" value={data.tickets_today} />
          <Kpi icon={Wallet} label="Ticket medio" value={`${data.average_ticket.toFixed(2)}€`} />
          <Kpi
            icon={ShieldCheck}
            label="Cadena Verifactu"
            value={chain ? `${chain.total_records} reg.` : "…"}
            tone={chain?.valid ? "emerald" : "red"}
            sub={chain?.valid ? "íntegra" : "rota"}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Top productos */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500">Top productos</div>
            <div className="mt-3 space-y-2">
              {data.top_products.length === 0 ? (
                <div className="text-sm text-slate-400">Sin ventas registradas hoy.</div>
              ) : (
                data.top_products.map((p, i) => (
                  <div key={p.name} className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="h-7 w-7 rounded-md bg-slate-100 text-slate-700 font-mono text-sm flex items-center justify-center">
                        {i + 1}
                      </div>
                      <div className="font-medium text-sm text-slate-800 truncate">{p.name}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono font-semibold text-slate-900">{p.total.toFixed(2)}€</div>
                      <div className="text-[11px] text-slate-500">{p.qty} ud</div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Por método */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500">Por método de pago</div>
            <div className="mt-3 space-y-2">
              {Object.entries(data.methods || {}).map(([k, v]) => {
                const Icon = METHOD_ICONS[k] || Wallet;
                return (
                  <div key={k} className="flex items-center justify-between p-2 rounded-md hover:bg-slate-50">
                    <div className="flex items-center gap-2 capitalize text-slate-700">
                      <Icon size={16} className="text-slate-400" /> {k}
                    </div>
                    <div className="font-mono font-semibold text-slate-900">{Number(v).toFixed(2)}€</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Por cajero */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500">Ventas por cajero (hoy)</div>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2">
            {data.by_cashier.length === 0 ? (
              <div className="text-sm text-slate-400">Sin actividad hoy.</div>
            ) : (
              data.by_cashier.map((c) => (
                <div key={c.name} className="p-3 rounded-md bg-slate-50 border border-slate-200">
                  <div className="text-xs text-slate-500">{c.name}</div>
                  <div className="font-display text-xl font-semibold text-slate-900 mt-1">{c.total.toFixed(2)}€</div>
                  <div className="text-xs text-slate-500">{c.tickets} tickets</div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Chain detail */}
        {chain && (
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-slate-500">
              <Link2 size={12} /> Cadena de huellas Verifactu
            </div>
            <div className="mt-2 text-sm">
              {chain.valid ? (
                <span className="text-emerald-700 font-medium">✓ Integridad correcta</span>
              ) : (
                <span className="text-red-700 font-medium">✗ Cadena rota en {JSON.stringify(chain.broken_at)}</span>
              )}
            </div>
            <div className="font-mono text-[11px] text-slate-500 mt-1 break-all">
              Última huella: {chain.last_hash}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub, tone }) {
  const toneCls =
    tone === "emerald"
      ? "bg-emerald-50 border-emerald-200 text-emerald-900"
      : tone === "red"
      ? "bg-red-50 border-red-200 text-red-900"
      : "bg-white border-slate-200 text-slate-900";
  return (
    <div className={`rounded-xl border p-4 ${toneCls}`}>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] opacity-70">
        <Icon size={14} /> {label}
      </div>
      <div className="mt-1 font-display text-2xl font-semibold">{value}</div>
      {sub && <div className="text-[11px] opacity-70 mt-0.5">{sub}</div>}
    </div>
  );
}
